import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Select, Spin } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled, GithubOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';
import taskonioMark from '../../assets/images/logo/favicon.svg';
import { OrganizationAccessRoles, setCurrentlyActiveOrganization, useGetUserFromAccessToken } from '../../common/auth';
import { getUserOrganizationAccess, USER_STATE_IN_ORGANIZATION, UserOrganizationAccess } from '../../api/organization';
import {
  useConfirmInstallationMutation,
  useGetGithubRepositories,
  useGetPendingInstallation,
} from '../../serverState/github';
import RoutePaths from '../../router/RoutePaths';
import RepositoryMappingTable from '../Settings/Integrations/RepositoryMappingTable';
import { forgetInstallOriginOrg, readInstallOriginOrg } from '../../common/githubInstallOrigin';

/**
 * Where GitHub's install callback lands.
 *
 * SECURITY: this screen is a control, not a formality. `GET /setup` verifies that whoever
 * completed the install controls the GitHub installation, but it cannot know which
 * Taskonio organization they meant - so it persists nothing and parks the result under a
 * single-use token. Anyone holding that token can hand it to someone else; if this screen
 * auto-confirmed from URL parameters, a link alone would bind an attacker's installation
 * into any organization its recipient administers.
 *
 * Three rules follow, and none of them may be relaxed:
 *   1. NEVER confirm without a deliberate human click.
 *   2. ALWAYS display what is being bound before binding it.
 *   3. Only offer organizations the user actually administers.
 *
 * The screen PRESELECTS the organization the install was started from, read from
 * sessionStorage (see common/githubInstallOrigin). That relaxes none of the three: the
 * click is still required, the verified GitHub account is still shown above it, and the
 * hint is only honoured after being matched against `adminOrgs` - so it can never
 * surface an organization the user does not administer, however it was tampered with.
 * It removes a choice, not a check. When it does not match, the picker is the fallback,
 * and it stays one click away regardless.
 *
 * DESIGN: this is a consent screen reached from outside the app, so it follows
 * `pages/Auth/OAuthAuthorize.tsx` - the app's only other consent screen - rather than the
 * in-app Settings shell: centred card, brand mark, full-width rounded actions. Actions are
 * antd `Button`s for the same reason OAuthAuthorize uses them: `BaseButton` renders a
 * `<div>`, and a keyboard-only user landing here from GitHub could not otherwise complete
 * the install at all.
 *
 * Colours come from the design-system scale, where `neutral` runs light → dark (neutral-100
 * is the page tint, neutral-1000 the strongest ink) and inverts under `data-theme="dark"`.
 * Body copy is `text-neutral-900`, headings `text-brand-grey-dark`. Tailwind preflight is
 * disabled repo-wide, so every bordered element needs an explicit `border-solid`.
 */
export default function GithubSetupComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingToken = searchParams.get('pending');
  const setupError = searchParams.get('error');

  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // Read once on mount rather than on every render: the value is cleared after a
  // successful confirm, and re-reading would change the screen out from under the user.
  const [originOrgId] = useState<number | null>(() => readInstallOriginOrg());
  // Set once the installation is bound. The screen then shows the mapping step instead
  // of navigating away - repositories are synced as part of completeInstallation, so
  // they exist by the time the confirm resolves.
  const [connectedOrg, setConnectedOrg] = useState<UserOrganizationAccess | null>(null);
  // A 400 means the token was never redeemed (unknown/expired/already spent) and the
  // server does NOT re-park it - unlike a 409, there is nothing to retry against. This
  // forces the same terminal "expired" card the initial peek-failure path uses below.
  const [tokenDead, setTokenDead] = useState(false);

  const pending = useGetPendingInstallation(setupError ? null : pendingToken);
  // Inert until the confirm lands (the hook is `enabled: !!organizationId`), and then the
  // same query key RepositoryMappingTable already uses - so the step below reads the
  // mapping count off the cache rather than issuing a second request.
  const repositories = useGetGithubRepositories(connectedOrg?.id ?? null);
  const confirmMutation = useConfirmInstallationMutation();
  const me = useGetUserFromAccessToken();

  const orgAccess = useQuery<UserOrganizationAccess[], Error>({
    queryKey: ['userOrganizationAccessForGithubSetup'],
    queryFn: async () => (await getUserOrganizationAccess()).data,
  });

  const userId = me.data?.user?.id;

  // Only organizations this user administers AND has ACTIVE access to. The server's
  // organizationGuard requires ACTIVE; `usersStateInOrganization` DEFAULTS to INVITED
  // (an invited-but-not-yet-activated admin), and without this second condition such an
  // org would be offered here only to have the confirm come back 403.
  const adminOrgs = useMemo(() => {
    if (!orgAccess.data || !userId) return [];
    return orgAccess.data.filter((org) =>
      org.OrganizationAccess?.some(
        (access) =>
          access.userId === userId &&
          access.roleToAssume === OrganizationAccessRoles.ADMIN &&
          access.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.ACTIVE
      )
    );
  }, [orgAccess.data, userId]);

  // The hint is only ever honoured through `adminOrgs`, which is the same list the
  // picker draws from - so rule 3 holds whatever sessionStorage contains.
  const originOrg = useMemo(
    () => (originOrgId === null ? undefined : adminOrgs.find((org) => org.id === originOrgId)),
    [adminOrgs, originOrgId]
  );

  // With one administered organization there is nothing to choose, so the screen states
  // the binding instead of offering a picker with a single row.
  const onlyOrg = adminOrgs.length === 1 ? adminOrgs[0] : undefined;

  useEffect(() => {
    const preselect = originOrg ?? onlyOrg;
    if (preselect) setSelectedOrgId(preselect.id);
  }, [originOrg, onlyOrg]);

  const card = (children: React.ReactNode, wide = false) => (
    // The page keeps the app surface (`bg-white` = --color-brand-white) and the card is
    // drawn with a border, not a fill. In this palette the tinted tokens are LIGHTER than
    // the surface in dark mode, so a tinted page behind a `bg-white` card inverts the
    // usual figure/ground under `data-theme="dark"`. Tint is reserved for the panels
    // sitting on top of the surface, which reads correctly in both themes.
    <div className="flex min-h-screen justify-center bg-white px-4 py-16">
      <div
        className={`h-fit w-full rounded-xl border border-solid border-neutral-300 p-8 ${
          wide ? 'max-w-2xl' : 'max-w-md'
        }`}
      >
        {children}
      </div>
    </div>
  );

  /** GitHub ⇄ Taskonio, so the screen says what it is before a word is read. */
  const connectorHeader = (linked: boolean) => (
    <div className="flex items-center justify-center gap-2">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-solid border-neutral-300 bg-brand-grey-light">
        <GithubOutlined className="text-2xl text-brand-grey-dark" />
      </span>
      <span className="h-px w-5 bg-neutral-400" />
      {linked ? (
        <CheckCircleFilled className="text-base text-brand-taskonio-green" />
      ) : (
        <ExclamationCircleFilled className="text-base text-neutral-700" />
      )}
      <span className="h-px w-5 bg-neutral-400" />
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-solid border-neutral-300 bg-brand-grey-light">
        <img src={taskonioMark} alt="" className="h-7 w-7" />
      </span>
    </div>
  );

  /** Every terminal state renders through here so they share one silhouette. */
  const statusCard = (title: string, body: React.ReactNode, action: React.ReactNode) =>
    card(
      <>
        {connectorHeader(false)}
        <h1 className="m-0 mt-6 text-center text-xl font-semibold leading-7 text-brand-grey-dark">{title}</h1>
        <p className="m-0 mt-3 text-center text-sm leading-relaxed text-neutral-900">{body}</p>
        <div className="mt-8">{action}</div>
      </>
    );

  const backHome = (
    <Button size="large" shape="round" block onClick={() => navigate(RoutePaths.Home)}>
      Back to Taskonio
    </Button>
  );

  // Checked before every other branch: once the installation is bound, nothing earlier
  // in this flow can still be relevant - the pending token is spent and the `error` and
  // `pending` query states describe a round trip that already finished.
  if (connectedOrg) {
    const goToIntegrations = () => {
      navigate(`/${connectedOrg.publicId}/settings/integrations`);
      // The active organization changed in localforage above; a reload is how the rest
      // of the app picks that up. Same two lines this screen used to run immediately
      // after the confirm - only the moment has moved.
      window.location.reload();
    };

    // The nudge, and its limit. A connected repository does nothing until it is mapped, so
    // Continue waits for one - but only while there is something to map. With no visible
    // repositories the requirement is unsatisfiable, and gating the only way forward on it
    // would strand the user on this screen.
    const visible = repositories.data ?? [];
    const mappedCount = visible.filter((repository) => repository.workspaceId !== null).length;
    const awaitingMapping = visible.length > 0 && mappedCount === 0;

    return card(
      <>
        {connectorHeader(true)}

        <h1 className="m-0 mt-6 text-center text-xl font-semibold leading-7 text-brand-grey-dark">GitHub connected</h1>
        <p className="m-0 mt-2 text-center text-sm leading-relaxed text-neutral-900">
          {/* This branch runs before the guard that requires `pending.data`, and the query
              holds its result for the session (staleTime: Infinity), so the account is
              present in practice. Named only when it actually is, rather than rendering a
              bare "@". */}
          {pending.data
            ? `${connectedOrg.name} is connected to @${pending.data.accountLogin}.`
            : `${connectedOrg.name} is connected.`}{' '}
          A connected repository does nothing until it is mapped to a space. Your choices save as you make them, so
          Continue only takes you on to Integrations settings.
        </p>

        <RepositoryMappingTable organizationId={connectedOrg.id} defaultExpanded={true} />

        <div className="mt-8">
          <Button type="primary" size="large" shape="round" block disabled={awaitingMapping} onClick={goToIntegrations}>
            Continue
          </Button>
          {/* A link rather than a second full-width button: this is the way past the nudge
              above, not an equal choice beside it. Same destination either way - what
              differs is whether anything was mapped on the way out. */}
          {awaitingMapping && (
            <div className="mt-2 text-center">
              <Button type="link" className="h-auto px-0 text-neutral-900" onClick={goToIntegrations}>
                Skip for now
              </Button>
            </div>
          )}
        </div>
      </>,
      true
    );
  }

  if (setupError) {
    return statusCard(
      "Couldn't connect GitHub",
      setupError === 'install_denied'
        ? 'We could not verify that you have access to that GitHub installation. Make sure you are signed in to the GitHub account that installed the app, then try again.'
        : 'Something went wrong while verifying the installation. Please try connecting again.',
      backHome
    );
  }

  if (!pendingToken) {
    return statusCard(
      'Nothing to confirm',
      'This page is where GitHub sends you after installing the Taskonio app. Start from Settings → Integrations.',
      backHome
    );
  }

  if (pending.isLoading || orgAccess.isLoading || me.isLoading) {
    return card(
      <div className="flex flex-col items-center gap-4 py-6">
        <Spin size="large" />
        <p className="m-0 text-sm text-neutral-900">Verifying your GitHub installation…</p>
      </div>
    );
  }

  if (orgAccess.isError || me.isError) {
    // me.isError collapses adminOrgs to [] exactly the way orgAccess.isError does (userId
    // is undefined either way) and deserves the same retry branch - without this it fell
    // through to the terminal "you need to be an admin" card, which offers no retry.
    return statusCard(
      "Couldn't load your organizations",
      'Something went wrong while checking which organizations you administer. Please try again.',
      <Button
        size="large"
        shape="round"
        block
        onClick={() => {
          if (orgAccess.isError) void orgAccess.refetch();
          if (me.isError) void me.refetch();
        }}
      >
        Retry
      </Button>
    );
  }

  if (pending.isError || !pending.data || tokenDead) {
    return statusCard(
      'This link has expired',
      // No specific minute count: the initial link's TTL and the shorter window given
      // after a failed confirm differ, and stating one number is wrong on the other path.
      "This installation link is no longer valid - it's past its window or was already used. Start again from Settings → Integrations.",
      backHome
    );
  }

  const onConfirm = async () => {
    if (!selectedOrgId) return;
    setConflict(null);
    try {
      await confirmMutation.mutateAsync({ organizationId: selectedOrgId, pendingToken });
      // Spent. Leaving it would preselect this organization on an unrelated later
      // install started from somewhere other than Settings -> Integrations.
      forgetInstallOriginOrg();
      const org = adminOrgs.find((o) => o.id === selectedOrgId);
      if (org) {
        // The settings routes read the active organization from localforage, not from the
        // URL's :orgId - see MenuSideBarBottom.tsx for the established pattern. Without
        // this, confirming into a non-active organization lands on the right URL but reads
        // the wrong org's data, so a successful install can report itself as a failure.
        await setCurrentlyActiveOrganization(org);
      }
      // Stay here and offer the mapping instead of bouncing to Settings. A repository
      // that is connected but unmapped does nothing at all, and this is the one moment
      // the user is certainly thinking about it. `org` is undefined only if adminOrgs
      // changed underneath a successful confirm; fall through to the old navigation
      // rather than render a step with no organization to map for.
      if (org) {
        setConnectedOrg(org);
        return;
      }
      navigate(`/${org?.publicId}/settings/integrations`);
      window.location.reload();
    } catch (err) {
      const status = (err as AxiosError)?.response?.status;
      if (status === 409) {
        // The server re-parks the token on a failed confirm, so the picker stays usable
        // and the user can bind it to a different organization without reinstalling.
        setConflict('That GitHub installation is already connected to another Taskonio organization.');
        return;
      }
      if (status === 403) {
        // The adminOrgs filter excludes non-ACTIVE access, but that check happens
        // against data that can go stale between page load and this click (access
        // deactivated, role changed) - the server is the final word. The token is
        // re-parked here too, same as the 409 case, so a different organization is
        // still a working retry.
        setConflict(
          'Your access to that organization is not active. Choose a different organization, or ask an admin to activate your access and try again.'
        );
        return;
      }
      if (status === 400) {
        // Nothing was redeemed and nothing is re-parked: the token is permanently dead.
        // Dead-end into the same terminal "expired" card the peek-failure path renders -
        // offering a Connect button here would be a retry that can never succeed.
        setTokenDead(true);
        return;
      }
      setConflict('Something went wrong connecting this installation. Please try again.');
    }
  };

  const detailRow = (label: string, value: React.ReactNode) => (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="m-0 shrink-0 text-sm text-neutral-900">{label}</dt>
      <dd className="m-0 min-w-0 truncate text-sm font-medium text-brand-grey-dark">{value}</dd>
    </div>
  );

  return card(
    <>
      {connectorHeader(true)}

      <h1 className="m-0 mt-6 text-center text-xl font-semibold leading-7 text-brand-grey-dark">Connect GitHub</h1>
      <p className="m-0 mt-2 text-center text-sm leading-relaxed text-neutral-900">
        {onlyOrg
          ? 'We verified this installation. Confirm the Taskonio organization it should belong to.'
          : 'We verified this installation. Choose the Taskonio organization it should belong to.'}
      </p>

      {/* Rule 2: what is being bound, shown before it is bound. */}
      <dl className="m-0 mt-6 flex flex-col gap-3 rounded-lg bg-brand-grey-light p-4">
        {detailRow('GitHub account', <>@{pending.data.githubLogin}</>)}
        {detailRow('Installation', pending.data.accountLogin)}
        {detailRow('Account type', pending.data.accountType)}
      </dl>

      {adminOrgs.length === 0 ? (
        <Alert
          className="mt-6"
          type="warning"
          showIcon={true}
          message="You need to be an admin of a Taskonio organization to connect a GitHub installation."
        />
      ) : onlyOrg ? (
        // Nothing to pick, so the checkmark is honest here: this is a statement of what
        // will be bound, and the Connect button below is still the deliberate act.
        <div className="mt-6">
          <p className="m-0 mb-2 text-sm font-medium text-brand-grey-dark">Taskonio organization</p>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-solid border-brand-taskonio-green bg-taskonioGreen-100 px-3 py-2.5">
            <span className="min-w-0 truncate text-sm text-brand-grey-dark">{onlyOrg.name}</span>
            <CheckCircleFilled className="shrink-0 text-brand-taskonio-green" />
          </div>
        </div>
      ) : (
        // One control for one decision. This replaced a preselected row with a checkmark
        // plus a "Connect to a different organization" link beside it - two affordances
        // for the same job, where the checkmark read as settled rather than editable.
        // A Select carries its own chevron, so the row says it can be changed without a
        // second control saying it, and it stays keyboard-operable, which matters on a
        // screen a user reaches from GitHub rather than from inside the app.
        <div className="mt-6">
          <p className="m-0 mb-2 text-sm font-medium text-brand-grey-dark">Connect it to which organization?</p>
          <Select
            className="w-full"
            size="large"
            value={selectedOrgId ?? undefined}
            placeholder="Choose an organization"
            onChange={(value: number) => setSelectedOrgId(value)}
            options={adminOrgs.map((org) => ({ value: org.id, label: org.name }))}
          />
        </div>
      )}

      {conflict && <Alert className="mt-4" type="error" showIcon={true} message={conflict} />}

      <div className="mt-8 flex flex-col gap-3">
        <Button
          type="primary"
          size="large"
          shape="round"
          block
          disabled={!selectedOrgId}
          loading={confirmMutation.isPending}
          onClick={() => void onConfirm()}
        >
          Connect
        </Button>
        <Button size="large" shape="round" block onClick={() => navigate(RoutePaths.Home)}>
          Cancel
        </Button>
      </div>
    </>
  );
}
